/**
 * Pure helpers for reading prices off a CdvPurchase (cordova-plugin-purchase
 * v13) product. No imports, so the tests can exercise them against the exact
 * offer shapes the Apple adapter builds (www/store.js, SKProduct.refresh).
 * Ported from the fleet reference 11-powederlog/src/services/iapPricing.js
 * (getBilledPricing, getIntroTrial, iso8601PeriodToDays) and the Wealth
 * Tracker port 15-wealth-tracker/src/services/iapPricing.ts
 * (dividePriceString, savingsPercent, priceMicros + billingPeriod on the
 * billed pricing). READ-ONLY: nothing here orders, restores or finishes.
 *
 * Guideline 3.1.2(c) — the BILLED amount must be the most conspicuous price
 * on the paywall. NEVER read it from `product.pricing.price`: that getter is
 * `offers[0].pricingPhases[0]`, and the Apple adapter puts the introductory
 * phase FIRST for any account eligible for the intro offer (every App Review
 * sandbox account is). On such an account `pricing.price` is the £0.00 free
 * trial, so PowderLog 1.2.0 build 11 rendered "£0.00" as its largest pricing
 * element and "Subscribe for £0.00/year" on the CTA — rejected 2026-09-08.
 * Canonical: shared-docs/in-app-purchases.md → Paywall Layout.
 */

// Parse an ISO-8601 billing period (P3D, P1W, P1M...) to days; null if unknown.
export function iso8601PeriodToDays(period) {
  const m = /^P(\d+)([DWMY])$/.exec(period || '');
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return { D: n, W: n * 7, M: n * 30, Y: n * 365 }[m[2]] ?? null;
}

// Whole months in a billing period (P1Y → 12, P6M → 6); null for day/week
// periods or anything unparseable. Drives the per-month line and the length.
export function periodToMonths(period) {
  const m = /^P(\d+)([MY])$/.exec(period || '');
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return m[2] === 'Y' ? n * 12 : n;
}

// The plan's length as Apple wants it stated (3.1.2(c): "/year" is not a
// length) — "12 months", "1 month", "1 week". Null when the period is unknown.
export function formatPlanLength(period) {
  const months = periodToMonths(period);
  if (months) return `${months} month${months === 1 ? '' : 's'}`;
  const m = /^P(\d+)([DW])$/.exec(period || '');
  if (!m) return null;
  const n = parseInt(m[1], 10);
  const unit = m[2] === 'W' ? 'week' : 'day';
  return `${n} ${unit}${n === 1 ? '' : 's'}`;
}

// The offer a plain `store.order(offer)` purchases: the adapter's Default
// offer when present, otherwise the first (Apple only emits an 'Introductory'
// offer in its place when the intro offer arrives via `discounts`).
function primaryOffer(product) {
  const offers = product?.offers || [];
  return offers.find((o) => o?.offerType === 'Default') || offers[0] || null;
}

// The recurring pricing phase — the amount charged every period once any
// trial / intro phase has run out. Returns { price, priceMicros, currency,
// billingPeriod } or null when the product has no priced phase (the caller
// falls back to its static default).
export function getBilledPricing(product) {
  const phases = primaryOffer(product)?.pricingPhases || [];
  if (!phases.length) return null;
  const recurring = phases.find((p) => p?.recurrenceMode === 'INFINITE_RECURRING');
  // Every adapter appends the base recurring price as the LAST phase; the
  // recurrenceMode match is the explicit form, the last phase the safety net.
  const phase = recurring || phases[phases.length - 1];
  if (!phase?.price) return null;
  return {
    price: phase.price,
    priceMicros:
      typeof phase.priceMicros === 'number' && Number.isFinite(phase.priceMicros) ? phase.priceMicros : null,
    currency: phase.currency || null,
    billingPeriod: phase.recurrenceMode === 'NON_RECURRING' ? null : phase.billingPeriod || null,
  };
}

// Detect a StoreKit introductory free trial on the offer a plain order buys.
// CdvPurchase v13 surfaces intro offers as pricing phases with paymentMode
// FREE_TRIAL (the enum value is 'FreeTrial'), and only while the Apple ID is
// eligible as far as the adapter knows. Returns { periodDays } or null.
// (The PowderLog original scans every offer; a trial on a promotional offer
// is not what `store.order(getOffer())` buys, so only the primary one counts.)
export function getIntroTrial(product) {
  for (const phase of primaryOffer(product)?.pricingPhases || []) {
    if (/free.?trial/i.test(String(phase?.paymentMode || ''))) {
      return { periodDays: iso8601PeriodToDays(phase.billingPeriod) };
    }
  }
  return null;
}

/**
 * Divide a StoreKit price string ("£89.99", "89,99 €", "¥12,000") into a
 * per-unit price in the SAME format — the storefront's currency symbol,
 * position and separators — e.g. dividePriceString("£89.99", 89990000, 12)
 * → "£7.50". The amount comes from StoreKit's micros when present (checked
 * against the parsed string); the formatting is lifted from the string.
 * Returns null whenever the string can't be parsed with confidence: canon
 * (in-app-purchases.md → Paywall Disclosure, rule 3) is to omit the per-unit
 * line rather than print a wrong number.
 */
export function dividePriceString(price, priceMicros, divisor) {
  if (!price || typeof price !== 'string' || !(divisor > 0)) return null;
  const first = price.search(/\d/);
  if (first < 0) return null;
  let last = first;
  for (let i = price.length - 1; i > first; i--) {
    if (/\d/.test(price[i])) {
      last = i;
      break;
    }
  }
  const prefix = price.slice(0, first);
  const numeric = price.slice(first, last + 1);
  const suffix = price.slice(last + 1);
  // Between the first and last digit only digits and separators may appear.
  if (!/^[\d.,'\s]*$/.test(numeric)) return null;

  // The decimal separator is the last '.' or ',' followed by 1–2 digits; three
  // digits after it make it a grouping separator ("¥4,000").
  const dec = /([.,])(\d{1,2})$/.exec(numeric);
  const decimalSep = dec ? dec[1] : null;
  const fractionDigits = dec ? dec[2].length : 0;
  const intPart = dec ? numeric.slice(0, numeric.length - dec[0].length) : numeric;
  const groupChars = intPart.replace(/\d/g, '');
  const groupSep = groupChars ? groupChars[0] : null;
  if (groupSep && (groupSep === decimalSep || [...groupChars].some((c) => c !== groupSep))) return null;

  const parsed = Number(`${intPart.replace(/\D/g, '')}${dec ? `.${dec[2]}` : ''}`);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  const micros =
    typeof priceMicros === 'number' && Number.isFinite(priceMicros) && priceMicros > 0 ? priceMicros : null;
  // StoreKit's own number disagrees with what we read off the string → we
  // misread the format. Omit.
  if (micros != null && Math.abs(micros / 1e6 - parsed) > Math.max(0.01, parsed * 0.005)) return null;

  const factor = 10 ** fractionDigits;
  const amount = Math.round(((micros != null ? micros / 1e6 : parsed) / divisor) * factor) / factor;
  if (!(amount > 0)) return null;
  const [whole, frac = ''] = amount.toFixed(fractionDigits).split('.');
  const grouped = groupSep ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, groupSep) : whole;
  return `${prefix}${grouped}${fractionDigits ? `${decimalSep}${frac}` : ''}${suffix}`;
}

/** Whole-percent saving of a yearly price over 12 monthly payments; null when there's none to claim. */
export function savingsPercent(monthlyMicros, yearlyMicros) {
  if (!monthlyMicros || !yearlyMicros || monthlyMicros <= 0 || yearlyMicros <= 0) return null;
  const pct = Math.round((1 - yearlyMicros / (monthlyMicros * 12)) * 100);
  return pct >= 1 ? pct : null;
}
